import React from 'react';

/**
 * The app has no icon package, so feature icons are inline SVG components
 * (same convention as shared/components/ActionIcons.tsx).
 */
export type McpIconProps = {
  className?: string;
  size?: number;
};

const base = (className: string | undefined, size: number) => ({
  className,
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false as const,
});

export const SearchIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)}>
    <circle cx="11" cy="11" r="6.4" />
    <path d="m20 20-4.5-4.5" />
  </svg>
);

export const PlusIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)}>
    <path d="M12 5.5v13M5.5 12h13" />
  </svg>
);

export const PlugIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)}>
    <path d="M9 3.5v5M15 3.5v5" />
    <path d="M6.5 8.5h11v2.4a5.5 5.5 0 0 1-5.5 5.5 5.5 5.5 0 0 1-5.5-5.5Z" />
    <path d="M12 16.4v4.1" />
  </svg>
);

export const PulseIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)}>
    <path d="M3.5 12.2h3.3l1.9-4.8 3.3 9.3 2.2-4.5h6.3" />
  </svg>
);

export const RadarIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)}>
    <circle cx="12" cy="12" r="8.3" />
    <circle cx="12" cy="12" r="3.4" />
    <path d="M12 12 18 6.4" />
  </svg>
);

export const CheckIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)} strokeWidth={2.1}>
    <path d="m5 12.8 4.4 4.2L19 7.4" />
  </svg>
);

export const PlayIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)} fill="currentColor">
    <path d="M8.5 6.2l9 5.8-9 5.8Z" />
  </svg>
);

export const TrashIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)}>
    <path d="M4.8 7.4h14.4" />
    <path d="M9.4 7.4V5.6h5.2v1.8" />
    <path d="M6.6 7.4l.8 11.1a1.4 1.4 0 0 0 1.4 1.3h6.4a1.4 1.4 0 0 0 1.4-1.3l.8-11.1" />
    <path d="M10.6 10.8v5.6M13.4 10.8v5.6" />
  </svg>
);

export const CloseIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)} strokeWidth={1.9}>
    <path d="m6.5 6.5 11 11M17.5 6.5l-11 11" />
  </svg>
);

export const ShieldIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)}>
    <path d="M12 3.8l6.7 2.4v5.2c0 4-2.7 7.2-6.7 8.6-4-1.4-6.7-4.6-6.7-8.6V6.2Z" />
    <path d="m9.4 12.1 1.9 1.9 3.4-3.6" />
  </svg>
);

export const AlertIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)}>
    <circle cx="12" cy="12" r="8.3" />
    <path d="M12 8v4.6M12 15.8h.01" />
  </svg>
);

export const ToolboxIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)}>
    <path d="M4 9.6h16v8.6a1.4 1.4 0 0 1-1.4 1.4H5.4A1.4 1.4 0 0 1 4 18.2Z" />
    <path d="M9 9.6V6.8a1.4 1.4 0 0 1 1.4-1.4h3.2A1.4 1.4 0 0 1 15 6.8v2.8" />
    <path d="M4 13.6h16" />
  </svg>
);

export const ChevronDownIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)} strokeWidth={1.9}>
    <path d="m6.5 9.8 5.5 5 5.5-5" />
  </svg>
);

export const ImportIcon: React.FC<McpIconProps> = ({ className, size = 16 }) => (
  <svg {...base(className, size)}>
    <path d="M12 4.4v9.8" />
    <path d="m8 10.6 4 3.8 4-3.8" />
    <path d="M5 17.4v1.2a1.4 1.4 0 0 0 1.4 1.4h11.2a1.4 1.4 0 0 0 1.4-1.4v-1.2" />
  </svg>
);
